import { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Button } from './Button';

interface ViewLike {
  _id: string;
  name: string;
}

interface EditViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  view: ViewLike | null;
  onRename: (id: string, newName: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function EditViewModal({ isOpen, onClose, view, onRename, onDelete }: EditViewModalProps) {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && view) {
      setName(view.name);
      setError('');
      setShowDeleteConfirm(false);
    }
  }, [isOpen, view]);

  const handleRename = async () => {
    if (!view || !name.trim()) return;
    if (name.trim() === view.name) {
      onClose();
      return;
    }
    try {
      setIsSubmitting(true);
      setError('');
      await onRename(view._id, name.trim());
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to rename view');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!view) return;
    try {
      setIsDeleting(true);
      setError('');
      await onDelete(view._id);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete view');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen || !view) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit View"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting || isDeleting}>
            Cancel
          </Button>
          <Button variant="primary" isLoading={isSubmitting} onClick={handleRename} disabled={isDeleting}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}
        <Input
          label="View Name"
          type="text"
          placeholder="View name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRename();
          }}
        />
        <div className="pt-3 border-t border-dark-border">
          {!showDeleteConfirm ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting}
            >
              Delete View
            </Button>
          ) : (
            <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-3">
              <p className="text-xs text-dark-text-secondary mb-3">
                This action cannot be undone. Are you sure you want to delete this view?
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDelete}
                  isLoading={isDeleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete View
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
